import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { Heading, isPlusPlan, cityToggleConfig } from '@spotahome/ui-library';

import {
  isAllBillsIncluded,
  isSomeBillsIncluded
} from '@spotahome/ui-library/src/utils/listing';
import { HomeCheckerShape } from '@spotahome/ui-library/src/tenant/shapes/HomeChecker';
import {
  LandlordScoringShape,
  RnaIdFeatureShape
} from '@spotahome/ui-library/src/tenant/constants/shapes';

import instantBookingShape from '@spotahome/marketplace-common/src/shapes/instantBooking';
import { usePricingContext } from '@spotahome/marketplace-common/src/context/PricingContext';

import './PropertyTitle.scss';

import ListingLabels from '../ListingLabels';

import ListingDoubts from '../ListingDoubts';

import RnaId from '../RnaId';

import PropertyDescriptionItem from './PropertyDescriptionItem';

const EMPTY_OBJECT = {};

const PropertyTitle = ({
  title,
  type,
  details,
  cityId,
  isBestChoice,
  plan,
  bills = EMPTY_OBJECT,
  isChecked,
  instantBooking = EMPTY_OBJECT,
  allowsPermanentContract = false,
  homechecker = null,
  verificationDate = null,
  landlordType,
  landlordId,
  listingId,
  countryCode,
  statistics = EMPTY_OBJECT,
  landlordScoring = EMPTY_OBJECT,
  rnaIdFeature = null
}) => {
  const pricingContext = usePricingContext();

  const getPropertyType = () =>
    type === 'room_shared_apt' ? 'flatmates' : 'bedrooms';

  return (
    <div className="property-title property-title--compact">
      <Heading
        level={1}
        theme="dark"
        size="large"
        align="left"
        className="property-title__heading"
        text={title}
        inline
        type="none"
      />
      <div className="property-title__details">
        <span>{trans(`listing.type.${type}`)}</span>
        <PropertyDescriptionItem
          value={details.nBedrooms}
          text={trans(`listing.property-title-section.${getPropertyType()}`, {
            n: details.nBedrooms
          })}
        />
        <PropertyDescriptionItem
          value={details.nBathrooms}
          text={trans('listing.property-title-section.bathrooms', {
            n: details.nBathrooms
          })}
        />
        {details.m2 > 0 && (
          <PropertyDescriptionItem value={details.m2} text="m2" />
        )}
      </div>
      <div className="property-title__statistics">
        {statistics.visited > 0 && (
          <PropertyDescriptionItem
            value={statistics.visited}
            iconName="eye-filled"
            text={trans('listing.statistics.views')}
          />
        )}
        {statistics.favorite > 0 && (
          <PropertyDescriptionItem
            value={statistics.favorite}
            iconName="fav_filled"
            text={trans('listing.statistics.favorites')}
          />
        )}
        {statistics.interested > 0 && (
          <PropertyDescriptionItem
            value={statistics.interested}
            iconName="person-filled"
            text={trans('listing.statistics.interests')}
          />
        )}
        {statistics.shared > 0 && (
          <PropertyDescriptionItem
            value={statistics.shared}
            iconName="shared"
            text={trans('listing.statistics.shares')}
          />
        )}
      </div>
      <ListingLabels
        isChecked={isChecked}
        hasNoDeposit={pricingContext?.hasNoDeposit}
        instantBooking={instantBooking}
        allBillsIncluded={isAllBillsIncluded(bills)}
        someBillsIncluded={isSomeBillsIncluded(bills)}
        isBestChoice={cityToggleConfig(cityId).isCoreCity && isBestChoice}
        isPlusPlan={isPlusPlan(plan)}
        allowsPermanentContract={allowsPermanentContract}
        homechecker={homechecker}
        verificationDate={verificationDate}
        landlordType={landlordType}
        landlordId={landlordId}
        landlordScoring={landlordScoring}
      />
      <RnaId rnaIdFeature={rnaIdFeature} />
      <ListingDoubts
        cityId={cityId}
        listingId={listingId}
        countryCode={countryCode}
        landlordId={landlordId}
      />
    </div>
  );
};

PropertyTitle.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  details: PropTypes.shape({
    nBedrooms: PropTypes.number,
    nBathrooms: PropTypes.number,
    m2: PropTypes.number
  }).isRequired,
  cityId: PropTypes.string.isRequired,
  isBestChoice: PropTypes.bool.isRequired,
  plan: PropTypes.string.isRequired,
  bills: PropTypes.shape({
    [PropTypes.number]: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
  }),
  isChecked: PropTypes.bool.isRequired,
  instantBooking: PropTypes.shape(instantBookingShape),
  allowsPermanentContract: PropTypes.bool,
  homechecker: PropTypes.shape(HomeCheckerShape),
  verificationDate: PropTypes.string,
  landlordType: PropTypes.oneOf(['professional', 'private']).isRequired,
  landlordId: PropTypes.string.isRequired,
  listingId: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  statistics: PropTypes.shape({
    visited: PropTypes.number,
    shared: PropTypes.number,
    favorite: PropTypes.number,
    interested: PropTypes.number
  }),
  landlordScoring: PropTypes.shape(LandlordScoringShape),
  rnaIdFeature: PropTypes.shape(RnaIdFeatureShape)
};

export default PropertyTitle;
